import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, type ComponentType } from 'react';
import PageMetadata from '@/components/PageMetadata';
import RouteSuspenseFallback from '@/components/RouteSuspenseFallback';

// Import layouts (not lazy – always needed for shell)
import PublicLayout from '@/layouts/PublicLayout';
import AuthLayout from '@/layouts/AuthLayout';
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout';

// Eagerly loaded – critical path pages
import HomePage from '@/features/games/pages/HomePage';
import UserLoginPage from '@/features/auth/pages/UserLoginPage';

/** Lazy import + reload khi chunk lỗi (deploy mới, cache cũ) */
function lazyReload(factory: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    factory().catch(() => {
      window.location.reload();
      return new Promise(() => {}) as Promise<{ default: ComponentType<any> }>;
    })
  );
}

// Lazy-loaded pages – code-split for smaller initial bundle
const SettingsPage = lazyReload(() => import('@/features/account/pages/SettingsPage'));
const AnimationSettingsPage = lazyReload(() => import('@/features/account/pages/AnimationSettingsPage'));
const AccountSecurityPage = lazyReload(() => import('@/features/account/pages/AccountSecurityPage'));
const MessagesPage = lazyReload(() => import('@/features/account/pages/MessagesPage'));
const GamesPage = lazyReload(() => import('@/features/games/pages/GamesPage'));
const GamePlayPage = lazyReload(() => import('@/features/games/pages/GamePlayPage'));
const DepositPage = lazyReload(() => import('@/features/finance/pages/DepositPage'));
const PaymentCheckoutPage = lazyReload(() => import('@/features/finance/pages/PaymentCheckoutPage'));
const WithdrawalLimitsLayout = lazyReload(() => import('@/features/finance/layouts/WithdrawalLimitsLayout'));
const WalletPage = lazyReload(() => import('@/features/finance/pages/WalletPage'));
const WalletTopUpPage = lazyReload(() => import('@/features/finance/pages/WalletTopUpPage'));
const WalletDepositCheckoutPage = lazyReload(() => import('@/features/finance/pages/WalletDepositCheckoutPage'));
const WalletPayoutRequestPage = lazyReload(() => import('@/features/finance/pages/WalletPayoutRequestPage'));
const TransactionsPage = lazyReload(() => import('@/features/finance/pages/TransactionsPage'));
const ActivatePage = lazyReload(() => import('@/features/auth/pages/ActivatePage'));
const ResetPasswordPage = lazyReload(() => import('@/features/auth/pages/ResetPasswordPage'));
const SupportPage = lazyReload(() => import('@/features/support/pages/SupportPage'));
const FAQPage = lazyReload(() => import('@/features/support/pages/FAQPage'));

function AppRouter() {
  return (
    <>
      <PageMetadata
        title="Kiosk Gaming"
        description="Professional Gaming Kiosk Platform"
      />
      <Suspense fallback={<RouteSuspenseFallback />}>
        <Routes>
          {/* Public Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
          </Route>

          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<UserLoginPage />} />
            <Route path="/activate" element={<ActivatePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Authenticated Routes - with Header and Footer */}
          <Route element={<AuthenticatedLayout />}>
            <Route path="/account" element={<Navigate to="/account/settings" replace />} />
            <Route path="/account/dashboard" element={<Navigate to="/account/settings" replace />} />
            <Route path="/account/settings" element={<SettingsPage />} />
            <Route path="/account/animations" element={<AnimationSettingsPage />} />
            <Route path="/account/security" element={<AccountSecurityPage />} />
            <Route path="/account/change-password" element={<Navigate to="/account/security" replace />} />
            <Route path="/account/email-phone" element={<Navigate to="/account/settings" replace />} />
            <Route path="/account/messages" element={<MessagesPage />} />
            <Route path="/account/games" element={<GamesPage />} />
            <Route path="/account/finance/deposit" element={<DepositPage />} />
            <Route path="/account/finance/payment-checkout" element={<PaymentCheckoutPage />} />
            {/* Legacy game-withdraw flow → Games (Withdraw opens GameWithdrawModal) */}
            <Route
              path="/account/finance/withdrawals"
              element={<Navigate to="/account/games" replace />}
            />
            <Route
              path="/account/finance/payout-request"
              element={<Navigate to="/account/games" replace />}
            />
            <Route element={<WithdrawalLimitsLayout />}>
              <Route path="/account/finance/wallet" element={<WalletPage />} />
              <Route path="/account/finance/wallet/deposit" element={<WalletTopUpPage />} />
              <Route path="/account/wallet/payout-request" element={<WalletPayoutRequestPage />} />
            </Route>
            <Route path="/account/wallet/deposit-checkout" element={<WalletDepositCheckoutPage />} />
            <Route
              path="/account/wallet/add-funds"
              element={<Navigate to="/account/finance/wallet/deposit" replace />}
            />
            <Route
              path="/account/finance/wallet/withdraw"
              element={<Navigate to="/account/wallet/payout-request" replace />}
            />
            <Route path="/account/transactions" element={<TransactionsPage />} />
            {/* Authenticated ticket view */}
            <Route path="/account/support/tickets/:ticketNumber" element={<SupportPage />} />
          </Route>

          {/* Standalone Game Player (no app shell) */}
          <Route path="/account/games/play" element={<GamePlayPage />} />

          {/* Support Routes */}
          <Route element={<PublicLayout />}>
            <Route path="/support" element={<SupportPage />} />
            <Route path="/support/tickets/:ticketNumber" element={<SupportPage />} />
            <Route path="/faq" element={<FAQPage />} />
          </Route>

          {/* Unknown paths → home (not account hub) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default AppRouter;
